
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Plus, Search } from "lucide-react";
import { useCountries } from "@/hooks/admin/countries/useCountries";
import { IDTypesTable } from "./IDTypesTable";
import { IDTypeFormDialog } from "./IDTypeFormDialog";
import { useIDTypes } from "../../../hooks/admin/identity-docs/useIDTypes";
import { useIsMobile } from "@/hooks/use-mobile";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { usePagination } from "@/hooks/polls/usePagination";

export function IDTypesList() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingIDType, setEditingIDType] = useState<any>(null);
  const { countries } = useCountries();
  const { idTypes, isLoading, error, deleteIDType } = useIDTypes();
  const isMobile = useIsMobile();

  // Filtering & Sorting state
  const [searchTerm, setSearchTerm] = useState("");
  const [countryFilter, setCountryFilter] = useState("all");
  const [attributeFilter, setAttributeFilter] = useState("all");
  const [sortField, setSortField] = useState<"document_name" | "country_code" | "created_at">("created_at");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [filteredTypes, setFilteredTypes] = useState<any[]>([]);

  // Pagination
  const {
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    totalPages,
    paginatedItems,
    startIndex,
    endIndex
  } = usePagination({
    totalItems: filteredTypes.length,
    initialPage: 1,
    itemsPerPage: 10
  });

  // Apply filters and sorting
  useEffect(() => {
    let result = [...idTypes];
    
    // Apply search filter
    if (searchTerm) {
      result = result.filter(type => 
        type.document_name.toLowerCase().includes(searchTerm.toLowerCase()) || 
        (type.description && type.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    // Apply country filter
    if (countryFilter !== 'all') {
      result = result.filter(type => type.country_code === countryFilter);
    }
    
    // Apply attribute filter
    if (attributeFilter !== 'all') {
      switch(attributeFilter) {
        case 'required':
          result = result.filter(type => type.required === true);
          break;
        case 'citizenship':
          result = result.filter(type => type.proves_citizenship === true);
          break;
        case 'residency':
          result = result.filter(type => type.proves_residency === true);
          break;
        case 'global':
          result = result.filter(type => type.global_document === true);
          break;
        case 'expatriate':
          result = result.filter(type => type.expatriate_identifier === true);
          break;
      }
    }
    
    // Apply sorting
    result.sort((a, b) => {
      const valueA = a[sortField];
      const valueB = b[sortField];
      
      if (valueA === valueB) return 0;
      
      const comparison = valueA < valueB ? -1 : 1;
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    
    setFilteredTypes(result);
    
    // Reset to first page when filters change
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [idTypes, searchTerm, countryFilter, attributeFilter, sortField, sortDirection]);

  const handleEdit = (idType: any) => {
    setEditingIDType(idType);
    setIsDialogOpen(true);
  };

  // Fixed handleDelete function to properly call the mutation
  const handleDelete = async (id: string) => {
    try {
      // Pass the id directly to deleteIDType.mutateAsync
      await deleteIDType.mutateAsync(id);
      toast.success("ID type deleted successfully");
    } catch (error: any) {
      toast.error(`Error deleting ID type: ${error.message}`);
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingIDType(null);
  };

  const handleSortChange = (value: string) => {
    const [field, direction] = value.split('-');
    setSortField(field as "document_name" | "country_code" | "created_at");
    setSortDirection(direction as "asc" | "desc");
  };

  const clearFilters = () => {
    setSearchTerm("");
    setCountryFilter("all");
    setAttributeFilter("all");
    setSortField("created_at");
    setSortDirection("desc");
  };

  const displayedItems = paginatedItems(filteredTypes);

  if (isLoading) {
    return <div className="flex justify-center p-8">
      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-nation-blue"></div>
    </div>;
  }

  if (error) {
    return <div className="p-4 text-destructive">Error loading ID types: {error.message}</div>;
  }

  return (
    <div className="space-y-6">
      <div className={`flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3`}>
        <h2 className="text-xl md:text-2xl font-bold tracking-tight">ID Document Types</h2>
        <Button onClick={() => setIsDialogOpen(true)} className="w-full sm:w-auto">
          <Plus className="h-4 w-4 mr-2" />
          Add ID Type
        </Button>
      </div>

      {/* Filters */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search by name or description..."
              className="pl-9"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          {/* Country Filter */}
          <Select value={countryFilter} onValueChange={setCountryFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by country" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Countries</SelectItem>
              {countries.map(country => (
                <SelectItem key={country.code} value={country.code}>{country.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Attribute Filter */}
          <Select value={attributeFilter} onValueChange={setAttributeFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by attribute" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Attributes</SelectItem>
              <SelectItem value="required">Required</SelectItem>
              <SelectItem value="citizenship">Proves Citizenship</SelectItem>
              <SelectItem value="residency">Proves Residency</SelectItem>
              <SelectItem value="global">Global Document</SelectItem>
              <SelectItem value="expatriate">Expatriate Identifier</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Sort */}
          <Select value={`${sortField}-${sortDirection}`} onValueChange={handleSortChange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="document_name-asc">Name (A-Z)</SelectItem>
              <SelectItem value="document_name-desc">Name (Z-A)</SelectItem>
              <SelectItem value="country_code-asc">Country (A-Z)</SelectItem>
              <SelectItem value="country_code-desc">Country (Z-A)</SelectItem>
              <SelectItem value="created_at-desc">Newest First</SelectItem>
              <SelectItem value="created_at-asc">Oldest First</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Clear Filters Button */}
          {(searchTerm || countryFilter !== 'all' || attributeFilter !== 'all' || sortField !== 'created_at' || sortDirection !== 'desc') && (
            <Button variant="outline" onClick={clearFilters}>
              Clear Filters
            </Button>
          )}
        </div>

        {/* Results Summary */}
        <div className="text-sm text-gray-500">
          {filteredTypes.length === 0 ? (
            <span>No ID types found matching your filters.</span>
          ) : (
            <span>Showing {startIndex + 1}-{Math.min(endIndex, filteredTypes.length)} of {filteredTypes.length} ID types</span>
          )}
        </div>
      </div>

      <IDTypesTable 
        idTypes={displayedItems}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
      
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center mt-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Items per page:</span>
            <Select value={itemsPerPage.toString()} onValueChange={(value) => setItemsPerPage(Number(value))}>
              <SelectTrigger className="w-[70px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5</SelectItem>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setCurrentPage(currentPage - 1)} 
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <span className="flex items-center px-2 text-sm">
              Page {currentPage} of {totalPages}
            </span>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setCurrentPage(currentPage + 1)} 
              disabled={currentPage >= totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}
      
      <IDTypeFormDialog
        open={isDialogOpen}
        onOpenChange={handleCloseDialog}
        countries={countries}
        idType={editingIDType}
      />
    </div>
  );
}
